import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown } from "lucide-react";

const HERO_IMG = "/hero-bg.jpg";

const lines = ["Buildings that", "stay affordable."];

const lineVariant = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1.05, ease: [0.16, 1, 0.3, 1], delay: 0.5 + i * 0.14 },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "28%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.05]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [0.44, 0.72]);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden grain"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0 z-0">
        <img
          src={HERO_IMG}
          alt="A North Carolina community at sunset"
          className="h-[120%] w-full object-cover"
        />
        <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-[#141A17]" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#141A17] via-transparent to-[#141A17]/30" />
      </motion.div>

      <div className="relative z-10 mx-auto max-w-[1400px] px-6 md:px-12 min-h-[100svh] flex flex-col justify-end pb-20 md:pb-28">
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.3 }}
          className="text-[11px] md:text-xs uppercase tracking-[0.32em] text-[#E8B4A0] mb-6"
          data-testid="hero-overline"
        >
          The Intersection of Private Equity &amp; Community
        </motion.p>

        <h1
          data-testid="hero-heading"
          className="font-heading text-white font-medium leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem] max-w-5xl"
        >
          {lines.map((line, i) => (
            <span key={i} className="reveal-mask">
              <motion.span custom={i} variants={lineVariant} initial="hidden" animate="visible" className="block">
                {i === 1 ? (
                  <>
                    stay <span className="italic font-normal text-[#E8B4A0]">affordable.</span>
                  </>
                ) : (
                  line
                )}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.15 }}
          className="mt-8 max-w-2xl text-base md:text-lg leading-relaxed text-white/80"
          data-testid="hero-subheading"
        >
          The Federation Companies acquires, preserves, and oversees HUD-assisted
          affordable housing across North Carolina and South Carolina — keeping
          working families and seniors housed in communities built to last.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.35 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollTo("#communities")}
            data-testid="hero-cta-communities"
            className="rounded-sm bg-white text-[#1C2220] text-sm px-7 py-3.5 hover:-translate-y-0.5 hover:bg-[#F7F5F0] transition-[transform,background-color] duration-300"
          >
            See Our Communities
          </button>
          <button
            onClick={() => scrollTo("#contact")}
            data-testid="hero-cta-about"
            className="rounded-sm border border-white/40 text-white text-sm px-7 py-3.5 hover:bg-white/10 transition-colors duration-300"
          >
            Partner With Us
          </button>
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollTo("#about")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 right-6 md:right-12 z-10 hidden md:flex items-center gap-2 text-white/60 hover:text-white transition-colors"
        data-testid="hero-scroll-indicator"
      >
        <span className="text-[10px] uppercase tracking-[0.3em]">Scroll</span>
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}>
          <ArrowDown size={16} />
        </motion.span>
      </motion.button>
    </section>
  );
};

export default Hero;
